import { h, Fragment } from 'preact';
import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import styles from './CollapsibleToggle.module.scss';
export function CollapsibleToggle({ title, children, defaultOpen = false, isCollapsible = true, }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const contentRef = useRef(null);
    // Force iOS Safari to recalculate layout on mount
    useEffect(() => {
        if (typeof window !== 'undefined' && window.innerWidth <= 768) {
            // Small delay to ensure component is fully rendered
            const timer = setTimeout(() => {
                if (contentRef.current) {
                    // Force a layout recalculation
                    contentRef.current.style.display = 'none';
                    contentRef.current.offsetHeight; // Force reflow
                    contentRef.current.style.display = '';
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []);
    const handleToggle = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isCollapsible) {
            setIsOpen(prev => !prev);
            // Fix iOS Safari initial page load viewport calculation bug
            if (typeof window !== 'undefined' && window.innerWidth <= 768) {
                setTimeout(() => {
                    // Force iOS Safari to recalculate viewport for all elements
                    const currentScroll = window.scrollY;
                    const currentHeight = document.body.scrollHeight;
                    // Trigger viewport recalculation
                    window.scrollTo(0, currentScroll + 1);
                    window.scrollTo(0, currentScroll);
                    // Force layout recalculation for the content
                    if (contentRef.current) {
                        contentRef.current.style.display = 'none';
                        contentRef.current.offsetHeight;
                        contentRef.current.style.display = '';
                    }
                    // Additional fix: force all collapsible elements to recalculate
                    const allCollapsibles = document.querySelectorAll(`.${styles.collapsible}`);
                    allCollapsibles.forEach((el) => {
                        const content = el.querySelector(`.${styles.content}`);
                        if (content) {
                            content.style.display = 'none';
                            content.offsetHeight;
                            content.style.display = '';
                        }
                    });
                }, 100);
            }
        }
    }, [isCollapsible]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle(e);
        }
    }, [handleToggle]);
    return (h("div", { className: `${styles.collapsible} ${isOpen ? styles.active : ''}` },
        h("button", { className: styles.header, onClick: handleToggle, onKeyDown: handleKeyDown, "aria-expanded": isOpen, type: "button", style: !isCollapsible ? { cursor: 'default' } : {} },
            h("span", { className: `${styles.title} ${isOpen ? styles.active : ''}` }, title),
            h("span", { className: styles.toggleIcon }, isOpen ? (h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "17", height: "2", viewBox: "0 0 17 2", fill: "none" },
                h("rect", { y: "0.5", width: "17", height: "1", fill: "#68735C" }))) : (h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "17", height: "18", viewBox: "0 0 17 18", fill: "none" },
                h("rect", { y: "8.5", width: "17", height: "1", fill: "#262525" }),
                h("rect", { x: "9", y: "0.5", width: "17", height: "1", transform: "rotate(90 9 0.5)", fill: "#262525" }))))),
        h("div", { ref: contentRef, className: `${styles.content} ${isOpen ? styles.isOpen : ''}` }, children)));
}
// Filter skeleton component for loading state (closed/collapsed state)
export function FilterSkeleton() {
    return (h("div", { className: styles.filterSkeleton },
        h("div", { className: styles.skeletonHeader },
            h("div", { className: styles.skeletonTitle }),
            h("div", { className: styles.skeletonIcon }))));
}
// Filter panel skeleton component
export function FilterPanelSkeleton({ filterCount = 5 }) {
    return (h(Fragment, null, Array.from({ length: filterCount }, (_, index) => (h(FilterSkeleton, { key: index })))));
}
